import PropTypes from 'prop-types';
import { useResolvedExperiment } from '@web/experiments';
import {
  BaseHeader,
  LeftMenuLinks,
  CenterMenuLinks,
  RotatingPromoBar,
  UserMenuLinks,
} from '@web/molecules';
import { useSessionCustomer } from '../customer';
import { MarketingHeaderWrapper } from './MarketingHeader.styles.js';
import { schema as rotatingPromoBarSchema } from './builders/rotating_promo_bar';

const mapCtaBlocks = blocks =>
  (blocks || []).map(button => ({
    mobileCTAText: button.mobileCTAText || button.mobile_cta_text,
    desktopCTAText: button.desktopCTAText || button.desktop_cta_text,
    ctaTarget:
      button.ctaTarget ||
      button.cta_target?.cached_url ||
      button.cta_target?.url,
    ctaVariant: button.ctaVariant || button.cta_variant,
    headerText: button.headerText || button.header_text,
    component: button.component,
  }));

export const MarketingHeader = ({
  mobileLinks,
  links,
  logoUrl,
  logoSrText,
  sticky = true,
  hidePromoBar,
  rotatingPromoBarProps,
  ctaButton,
  mobileCtaButton,
  logoImg,
  bgColor = 'background.dark',
  menuBgColor,
  fontColor = 'text.lightest',
  hideLoginLinkMobile,
  hideLoginLinkDesktop = false,
  mobileLogo,
  closeButtonPosition = 'left',
  closeButtonIconColor,
  closeButtonBackgroundColor,
  closeButtonBorderColor,
  fullBleedNav = false,
  fullBleedLogo = null,
}) => {
  const { isActive } = useSessionCustomer();

  const mobileExperimentLinks = useResolvedExperiment(
    mobileLinks,
    (blocks, fallback) => blocks?.[0]?.navigation_links || fallback,
  );
  const experimentLinks = useResolvedExperiment(
    links,
    (blocks, fallback) => blocks?.[0]?.navigation_links || fallback,
  );
  const experimentCtaButton = useResolvedExperiment(ctaButton, mapCtaBlocks);
  const mobileExperimentCtaButton = useResolvedExperiment(
    mobileCtaButton,
    mapCtaBlocks,
  );
  const resolvedRotatingPromoBarProps = useResolvedExperiment(
    rotatingPromoBarProps,
    blocks => (blocks?.[0] ? rotatingPromoBarSchema.cast(blocks[0]) : null),
  );

  const topHeaderSegment =
    !hidePromoBar && !isActive && resolvedRotatingPromoBarProps ? (
      <RotatingPromoBar {...resolvedRotatingPromoBarProps} />
    ) : null;

  const stickyBanner =
    !!topHeaderSegment && (resolvedRotatingPromoBarProps?.sticky || false);

  const rightNavSegment = (
    <>
      <UserMenuLinks
        ctas={experimentCtaButton}
        hideLoginLink={hideLoginLinkDesktop}
      />
      <LeftMenuLinks
        links={mobileExperimentLinks}
        menuBgColor={menuBgColor}
        ctas={mobileExperimentCtaButton ?? experimentCtaButton}
        hideLoginLink={hideLoginLinkMobile}
        logo={mobileLogo}
        closeButtonPosition={closeButtonPosition}
        closeButtonIconColor={closeButtonIconColor}
        closeButtonBackgroundColor={closeButtonBackgroundColor}
        closeButtonBorderColor={closeButtonBorderColor}
      />
    </>
  );
  const centerNavSegment = <CenterMenuLinks links={experimentLinks} />;

  return (
    <MarketingHeaderWrapper>
      <BaseHeader
        logoImg={logoImg}
        fullBleedLogo={fullBleedLogo}
        logoUrl={logoUrl}
        logoSrText={logoSrText}
        topSegment={topHeaderSegment}
        rightSegment={rightNavSegment}
        centerSegment={centerNavSegment}
        sticky={sticky}
        stickyBanner={stickyBanner}
        hasBanner={!!topHeaderSegment}
        bgColor={bgColor}
        fontColor={fontColor}
        headerVerticalPadding={5}
        headerHorizontalPadding={3}
        fullBleedNav={fullBleedNav}
      />
    </MarketingHeaderWrapper>
  );
};

MarketingHeader.displayName = 'MarketingHeader';

MarketingHeader.propTypes = {
  links: PropTypes.array.isRequired,
  logoImg: PropTypes.object,
  logoUrl: PropTypes.string,
  logoSrText: PropTypes.string,
  sticky: PropTypes.bool,
  hidePromoBar: PropTypes.bool,
  ctaButton: PropTypes.array,
  testName: PropTypes.string,
  bgColor: PropTypes.string,
  fontColor: PropTypes.string,
  menuBgColor: PropTypes.string,
  hideLoginLinkMobile: PropTypes.bool,
  hideLoginLinkDesktop: PropTypes.bool,
  mobileLogo: PropTypes.object,
  closeButtonPosition: PropTypes.oneOf(['left', 'right']),
  closeButtonIconColor: PropTypes.string,
  closeButtonBackgroundColor: PropTypes.string,
  closeButtonBorderColor: PropTypes.string,
  fullBleedNav: PropTypes.bool,
  fullBleedLogo: PropTypes.object,
};
